import { useState, useEffect, useRef, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Header from "@/components/Header";
import CreditBadge from "@/components/CreditBadge";
import CostDisclaimer from "@/components/CostDisclaimer";
import ConfirmationCard from "@/components/ConfirmationCard";
import SuccessCard from "@/components/SuccessCard";
import { apiClient, type EpisodeCountResponse } from "@/api/client";
import { useCredits } from "@/hooks/useCredits";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

const formatCachedAt = (value?: string | null) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const describeCacheState = (details?: EpisodeCountResponse | null) => {
  if (!details) return null;
  if (details.refreshed) {
    return "We just refreshed this feed so the episode count is up to date.";
  }
  const cachedLabel = formatCachedAt(details.cached_at);
  if (cachedLabel) {
    return `Episode count from cache last updated ${cachedLabel}.`;
  }
  return null;
};

const numberFormatter = new Intl.NumberFormat();

const applyCredentialsToUrl = (feedUrl: string, username: string, password: string) => {
  const trimmedUrl = feedUrl.trim();
  const trimmedUsername = username.trim();

  if (!trimmedUrl || (!trimmedUsername && !password)) {
    return trimmedUrl;
  }

  try {
    const url = new URL(trimmedUrl);
    if (trimmedUsername) {
      url.username = trimmedUsername;
    }
    if (password) {
      url.password = password;
    }
    return url.toString();
  } catch {
    return trimmedUrl;
  }
};

async function deriveIdempotencyKey(feedUrl: string) {
  const trimmedUrl = feedUrl.trim();
  const payload = trimmedUrl;
  const encoder = new TextEncoder();

  const globalCrypto: Crypto | undefined =
    typeof window !== "undefined" && window.crypto ? window.crypto : typeof crypto !== "undefined" ? crypto : undefined;

  if (globalCrypto?.subtle) {
    try {
      const data = encoder.encode(payload);
      const digest = await globalCrypto.subtle.digest("SHA-256", data);
      return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
    } catch (err) {
      console.warn("Failed to derive SHA-256 idempotency key, falling back to legacy hash", err);
    }
  }

  // Fallback: simple deterministic hash (FNV-1a style) that remains stable between requests.
  let hash = 0x811c9dc5;
  for (let index = 0; index < payload.length; index += 1) {
    hash ^= payload.charCodeAt(index);
    hash = (hash * 0x01000193) >>> 0;
  }
  return `rss-${hash.toString(16).padStart(8, "0")}`;
}

export default function AddPodcastPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { creditBalance, refreshCredits } = useCredits();

  const [feedUrl, setFeedUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showCredentials, setShowCredentials] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [preview, setPreview] = useState<EpisodeCountResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Two-step flow state
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Subscription state
  const [subscribeMessage, setSubscribeMessage] = useState<string | null>(null);
  const [subscribeStatus, setSubscribeStatus] = useState<"idle" | "pending" | "success" | "error">("idle");
  const [amountChargedCents, setAmountChargedCents] = useState<number | undefined>(undefined);
  const [creditsUsedCents, setCreditsUsedCents] = useState<number | undefined>(undefined);

  const hasInitializedUrl = useRef(false);

  // URL state persistence - read from query params or sessionStorage
  useEffect(() => {
    const urlFromParams = searchParams.get("url");
    const usernameFromParams = searchParams.get("username");
    const passwordFromParams = searchParams.get("password");
    
    if (urlFromParams) {
      const decodedUrl = decodeURIComponent(urlFromParams);
      setFeedUrl(decodedUrl);
      // Also store in sessionStorage for consistency
      sessionStorage.setItem("pendingPodcastUrl", decodedUrl);
      hasInitializedUrl.current = true;
    } else if (!hasInitializedUrl.current) {
      // Only use sessionStorage on initial load
      const storedUrl = sessionStorage.getItem("pendingPodcastUrl");
      if (storedUrl) {
        setFeedUrl(storedUrl);
      }
      hasInitializedUrl.current = true;
    }

    // Restore username and password from query params if they exist
    if (usernameFromParams) {
      setUsername(decodeURIComponent(usernameFromParams));
      setShowCredentials(true);
    }
    if (passwordFromParams) {
      setPassword(decodeURIComponent(passwordFromParams));
      setShowCredentials(true);
    }
  }, [searchParams]);
  const [topupStatus, setTopupStatus] = useState<"idle" | "pending" | "success" | "error">("idle");
  const [paymentMethodUrl, setPaymentMethodUrl] = useState<string | null>(null);

  const resetPreviewState = () => {
    setPreview(null);
    setSubscribeMessage(null);
    setSubscribeStatus("idle");
    setShowConfirmation(false);
    setTopupStatus("idle");
    setPaymentMethodUrl(null);
    setAmountChargedCents(undefined);
    setCreditsUsedCents(undefined);
  };

  const resetForm = () => {
    setFeedUrl("");
    setUsername("");
    setPassword("");
    setShowCredentials(false);
    resetPreviewState();
    sessionStorage.removeItem("pendingPodcastUrl");
  };

  // Store URL in sessionStorage when user enters it
  useEffect(() => {
    if (feedUrl.trim()) {
      sessionStorage.setItem("pendingPodcastUrl", feedUrl.trim());
    }
  }, [feedUrl]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedUrl = feedUrl.trim();

    if (!trimmedUrl) {
      setError("Please enter a podcast feed URL.");
      resetPreviewState();
      return;
    }

    setIsChecking(true);
    setError(null);
    resetPreviewState();

    const urlWithCredentials = showCredentials ? applyCredentialsToUrl(trimmedUrl, username, password) : trimmedUrl;

    try {
      const result = await apiClient.resolveEpisodeCount(urlWithCredentials);
      setPreview(result);
    } catch (err) {
      console.error("Failed to resolve feed URL", err);
      setError(
        err instanceof Error
          ? err.message || "We could not check that feed. Please try again or confirm the URL."
          : "We could not check that feed. Please try again or confirm the URL.",
      );
    } finally {
      setIsChecking(false);
    }
  };

  const handleSubscribeWithPayment = async () => {
    if (!preview) return;

    // If creditBalance is null (endpoint 404'd), just subscribe directly
    if (creditBalance === null) {
      await handleSubscribe();
      return;
    }

    const costCents = Math.ceil(preview.episode_count * 1.7);
    const neededCents = Math.max(0, costCents - creditBalance);

    if (neededCents <= 0) {
      // Sufficient credits - track credits used
      setCreditsUsedCents(costCents);
      setAmountChargedCents(0);
      await handleSubscribe();
      return;
    }

    // Need topup
    setTopupStatus("pending");
    try {
      const topupResult = await apiClient.topupCredits(neededCents);
      if (topupResult.payment_status === "paid") {
        setTopupStatus("success");
        // Track payment (creditBalance is the pre-topup value)
        setAmountChargedCents(neededCents);
        setCreditsUsedCents(creditBalance ?? 0);
        await handleSubscribe();
      } else {
        throw new Error("Payment not completed immediately.");
      }
    } catch (err) {
      console.error("Topup failed", err);
      setTopupStatus("error");
      // Create payment method update session
      try {
        const currentUrl = feedUrl.trim() || sessionStorage.getItem("pendingPodcastUrl") || "";
        const encodedUrl = encodeURIComponent(currentUrl);
        const redirectParams = new URLSearchParams({ url: encodedUrl });
        if (username.trim()) {
          redirectParams.set("username", encodeURIComponent(username.trim()));
        }
        if (password.trim()) {
          redirectParams.set("password", encodeURIComponent(password.trim()));
        }
        const redirectPath = `/podcasts/add?${redirectParams.toString()}`;
        const encodedRedirectPath = encodeURIComponent(redirectPath);
        const session = await apiClient.updatePaymentMethod(
          `${window.location.origin}/billing/payment-method/success?redirect=${encodedRedirectPath}`,
          `${window.location.origin}/billing/payment-method/failure?redirect=${encodedRedirectPath}`,
        );
        setPaymentMethodUrl(session.checkout_url);
      } catch (sessionErr) {
        console.error("Failed to create payment method session", sessionErr);
      }
    }
  };

  const handleSubscribe = async () => {
    if (!preview) {
      return;
    }

    setSubscribeStatus("pending");
    setSubscribeMessage(null);

    try {
      const trimmedFeedUrl = feedUrl.trim();
      const urlWithCredentials =
        showCredentials ? applyCredentialsToUrl(trimmedFeedUrl, username, password) : trimmedFeedUrl;
      const idempotencyKey = await deriveIdempotencyKey(urlWithCredentials);

      const result = await apiClient.subscribeToFeed(urlWithCredentials, {
        idempotencyKey,
      });

      if (!result.ok || !result.subscribed) {
        const message = result.error?.trim() || "We couldn't subscribe to that feed just yet.";
        throw new Error(message);
      }

      setSubscribeStatus("success");
      const acceptedUrl = result.subscribed.accepted_url?.trim() || urlWithCredentials || trimmedFeedUrl;
      let hostname: string | null = null;
      if (acceptedUrl) {
        try {
          hostname = new URL(acceptedUrl).hostname;
        } catch {
          hostname = null;
        }
      }
      const displayName =
        preview?.display_name?.trim() || (hostname ? `the feed on ${hostname}` : "your podcast");
      const queuedFromPreview =
        typeof preview?.episode_count === "number" && preview.episode_count > 0 ? preview.episode_count : null;
      const formattedCount = queuedFromPreview ? numberFormatter.format(queuedFromPreview) : null;
      const backfillNote =
        result.status === "backfill_pending"
          ? " We're fetching the full back catalogue before we send your notification."
          : "";
      setSubscribeMessage(
        formattedCount
          ? `All set! We queued approximately ${formattedCount} episodes from ${displayName}. You'll receive an email when your podcast is indexed and ready for chatting.${backfillNote}`
          : `All set! ${displayName} is in the queue. You'll receive an email when your podcast is indexed and ready for chatting.${backfillNote}`,
      );

      // Clear form and stored state
      resetForm();

      // Refresh credit balance from context
      await refreshCredits();
    } catch (err) {
      console.error("Failed to subscribe to feed", err);
      setSubscribeStatus("error");
      setShowConfirmation(false);
      const detail = err instanceof Error ? err.message.trim() : "";
      const baseMessage = "We couldn't subscribe right now. Please double-check the feed details and try again in a moment.";
      setSubscribeMessage(detail && detail !== baseMessage ? `${baseMessage} (${detail})` : baseMessage);
    }
  };

  const costCents = preview ? Math.ceil(preview.episode_count * 1.7) : 0;
  const neededCents = creditBalance !== null ? Math.max(0, costCents - creditBalance) : 0;
  const cacheStatusMessage = describeCacheState(preview);

  return (
    <div className={cn("min-h-screen flex flex-col", theme.colors.backgroundCream)}>
      <Header />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-10">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={cn(
              "mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200",
              theme.borders.neubrutalism,
              theme.shadows.neubrutalismSm,
              "bg-white/90 dark:bg-gray-800/90 transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-primary-orange/40 dark:focus:ring-primary-orange-dark/30",
            )}
          >
            <span aria-hidden="true">←</span>
            Back
          </button>

          <section className={cn(theme.cards.base, "p-8 space-y-8")}>
            <header>
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">Add a podcast feed</h1>
              <p className="mt-2 text-base text-gray-600 dark:text-gray-300">
                Paste the RSS feed URL for the show you'd like to follow. We'll verify it and let you know how many
                episodes are ready to add.
              </p>
            </header>

            {/* Step 3: Success State */}
            {subscribeStatus === "success" && subscribeMessage && (
              <SuccessCard
                podcastName={preview?.display_name || "your podcast"}
                episodeCount={preview?.episode_count || null}
                successMessage={subscribeMessage}
                amountChargedCents={amountChargedCents}
                creditsUsedCents={creditsUsedCents}
                onAddAnother={resetForm}
              />
            )}

            {/* Error State */}
            {error && (
              <div className={cn("rounded-2xl px-4 py-3 text-sm font-medium", theme.borders.error, theme.colors.errorBg, theme.text.error)}>
                {error}
              </div>
            )}

            {/* Payment Error State */}
            {topupStatus === "error" && (
              <div className={cn("rounded-2xl px-5 py-4 space-y-4", theme.borders.error, theme.colors.errorBg, theme.text.error)}>
                <h3 className="text-xl font-semibold">Payment failed</h3>
                <p className="text-sm sm:text-base leading-relaxed">
                  We couldn't process the payment of ${(neededCents / 100).toFixed(2)}. Please update your payment
                  method to continue.
                </p>
                {paymentMethodUrl && (
                  <a
                    href={paymentMethodUrl}
                    className={cn(
                      "inline-flex items-center justify-center rounded-full px-8 py-3 text-base font-semibold min-h-[3.25rem]",
                      theme.buttons.primary,
                    )}
                  >
                    Update Payment Method
                  </a>
                )}
              </div>
            )}

            {/* Subscription Error State */}
            {subscribeStatus === "error" && subscribeMessage && (
              <div className={cn("rounded-2xl px-5 py-4 space-y-4", theme.borders.error, theme.colors.errorBg, theme.text.error)}>
                <h3 className="text-xl font-semibold">We couldn't subscribe</h3>
                <p className="text-sm sm:text-base leading-relaxed">{subscribeMessage}</p>
                <button
                  type="button"
                  onClick={() => {
                    setSubscribeStatus("idle");
                    setSubscribeMessage(null);
                    setShowConfirmation(false);
                  }}
                  className={cn(
                    theme.buttons.secondary,
                    "rounded-full px-5 py-2 text-sm font-semibold min-h-[2.5rem] text-gray-900 dark:text-white",
                  )}
                >
                  Try again
                </button>
              </div>
            )}

            {/* Step 2: Confirmation Card */}
            {showConfirmation && preview && subscribeStatus !== "success" && topupStatus !== "error" && subscribeStatus !== "error" && (
              <ConfirmationCard
                podcastName={preview.display_name}
                episodeCount={preview.episode_count}
                estimatedCostCents={costCents}
                creditBalanceCents={creditBalance}
                amountToChargeCents={neededCents}
                onConfirm={handleSubscribeWithPayment}
                isProcessing={subscribeStatus === "pending" || topupStatus === "pending"}
              />
            )}

            {/* Step 1: Preview Card */}
            {preview && !showConfirmation && subscribeStatus !== "success" && topupStatus !== "error" && subscribeStatus !== "error" && (
              <div className="space-y-4">
                <div
                  className={cn(
                    "flex flex-col gap-3 rounded-2xl px-5 py-4",
                    theme.borders.neubrutalism,
                    theme.shadows.neubrutalism,
                    theme.colors.cardCream,
                  )}
                >
                  <span
                    className={cn(
                      "inline-flex items-center self-start rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-900 dark:text-white",
                      theme.colors.primaryOrange,
                      theme.borders.neubrutalism,
                      theme.shadows.neubrutalismSm,
                    )}
                  >
                    Podcast found!
                  </span>
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{preview.display_name}</h2>
                  <div className="flex items-center gap-3 text-base text-gray-800 dark:text-gray-200">
                    <span className="text-lg font-semibold">{preview.episode_count}</span>
                    episodes ready to import
                  </div>
                  {cacheStatusMessage && (
                    <p className="text-xs text-gray-600 dark:text-gray-400">{cacheStatusMessage}</p>
                  )}
                </div>

                <div className={cn("rounded-2xl px-5 py-4 space-y-4", theme.borders.neubrutalism, "bg-white/90 dark:bg-gray-900/50", theme.shadows.neubrutalismSm)}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                      {creditBalance !== null && <CreditBadge balanceCents={creditBalance} />}
                      <div>
                        <p className="text-base font-semibold text-gray-900 dark:text-white">
                          Estimated cost: ~${(costCents / 100).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <CostDisclaimer />

                  <button
                    type="button"
                    onClick={() => setShowConfirmation(true)}
                    className={cn(
                      "w-full inline-flex items-center justify-center rounded-full px-8 py-3 text-base font-semibold min-h-[3.25rem]",
                      theme.buttons.secondary,
                    )}
                  >
                    Confirm Understanding of Costs
                  </button>
                </div>
              </div>
            )}

            {/* Form - only show if no preview or in error state */}
            {(!preview || subscribeStatus === "error") && topupStatus !== "error" && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="feed-url" className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    Podcast feed URL
                  </label>
                  <div
                    className={cn(
                      "flex items-center rounded-2xl px-4 py-2 bg-white/90 dark:bg-gray-900/60",
                      theme.borders.neubrutalism,
                      theme.shadows.neubrutalism,
                      "focus-within:ring-4 focus-within:ring-primary-orange/40 dark:focus-within:ring-primary-orange-dark/30",
                    )}
                  >
                    <input
                      id="feed-url"
                      type="url"
                      required
                      inputMode="url"
                      placeholder="https://example.com/feed.rss"
                      className="flex-1 bg-transparent text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none"
                      value={feedUrl}
                      onChange={event => {
                        setFeedUrl(event.target.value);
                        resetPreviewState();
                      }}
                      autoComplete="off"
                      spellCheck="false"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  <button
                    type="button"
                    onClick={() => setShowCredentials(prev => !prev)}
                    className={cn(
                      "inline-flex items-center gap-2 self-start rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-primary-orange/40 dark:focus:ring-primary-orange-dark/30",
                      theme.colors.cardCream,
                      theme.borders.neubrutalism,
                      theme.shadows.neubrutalismSm,
                    )}
                  >
                    <span aria-hidden="true">{showCredentials ? "-" : "+"}</span>
                    {showCredentials ? "Hide username & password" : "Add username & password"}
                  </button>

                  {showCredentials && (
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <label htmlFor="feed-username" className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                          Username
                        </label>
                        <input
                          id="feed-username"
                          type="text"
                          autoComplete="username"
                          className={cn(
                            "w-full rounded-2xl px-4 py-2 bg-white/90 dark:bg-gray-900/60 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:ring-4 focus:ring-primary-orange/40 dark:focus:ring-primary-orange-dark/30",
                            theme.borders.neubrutalism,
                            theme.shadows.neubrutalism,
                          )}
                          placeholder="Optional"
                          value={username}
                          onChange={event => setUsername(event.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="feed-password" className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                          Password
                        </label>
                        <input
                          id="feed-password"
                          type="text"
                          autoComplete="current-password"
                          className={cn(
                            "w-full rounded-2xl px-4 py-2 bg-white/90 dark:bg-gray-900/60 text-base text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-4 focus:ring-primary-orange/40 dark:focus:ring-primary-orange-dark/30",
                            theme.borders.neubrutalism,
                            theme.shadows.neubrutalism,
                          )}
                          placeholder="Optional"
                          value={password}
                          onChange={event => setPassword(event.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <button
                    type="submit"
                    className={cn(
                      theme.buttons.primary,
                      "inline-flex items-center justify-center rounded-full px-8 py-3 text-base font-semibold min-h-[3.25rem]",
                    )}
                    disabled={isChecking}
                  >
                    {isChecking ? "Checking feed..." : "Check feed"}
                  </button>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    We'll preview the podcast details and episodes before subscribing.
                  </p>
                </div>
              </form>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
